import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useTelegram } from '../../hooks/useTelegram';

const DebugContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-300px'};
  width: 300px;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 20px;
  transition: right 0.3s ease;
  z-index: 9999;
  overflow-y: auto;
  border-left: 2px solid #00ff00;
`;

const DebugToggle = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: #00ff00;
  border: 1px solid #00ff00;
  padding: 8px 12px;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  cursor: pointer;
  z-index: 10000;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const DebugSection = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid #333;
  padding-bottom: 10px;
`;

const DebugTitle = styled.h3`
  margin: 0 0 10px 0;
  color: #ffff00;
  font-size: 14px;
`;

const DebugItem = styled.div`
  margin: 5px 0;
  word-break: break-all;
`;

const StatusBadge = styled.span<{ status: 'success' | 'error' | 'warning' }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  background: ${props =>
    props.status === 'success' ? '#00ff00' :
    props.status === 'error' ? '#ff0000' : '#ffaa00'
  };
  color: black;
  margin-left: 5px;
`;

export const DebugOverlay: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const auth = useAuth();
  const telegram = useTelegram();

  const getStatusBadge = (condition: boolean) => (
    <StatusBadge status={condition ? 'success' : 'error'}>
      {condition ? 'OK' : 'FAIL'}
    </StatusBadge>
  );

  const formatJson = (obj: any) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <>
      <DebugToggle onClick={() => setIsOpen(!isOpen)}>
        DEBUG {isOpen ? '×' : '→'}
      </DebugToggle>

      <DebugContainer isOpen={isOpen}>
        <DebugSection>
          <DebugTitle>🔐 Authentication Status</DebugTitle>
          <DebugItem>
            Is Authenticated: {auth.isAuthenticated ? 'YES' : 'NO'}
            {getStatusBadge(auth.isAuthenticated)}
          </DebugItem>
          <DebugItem>
            Is Loading: {auth.isLoading ? 'YES' : 'NO'}
          </DebugItem>
          <DebugItem>
            Is Telegram WebApp: {auth.isTelegramWebApp ? 'YES' : 'NO'}
            {getStatusBadge(auth.isTelegramWebApp)}
          </DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>👤 User Data</DebugTitle>
          {auth.user ? (
            <>
              <DebugItem>ID: {auth.user.id}</DebugItem>
              <DebugItem>Name: {auth.user.first_name} {auth.user.last_name || ''}</DebugItem>
              <DebugItem>Username: @{auth.user.username || 'N/A'}</DebugItem>
              <DebugItem>Language: {auth.user.language_code || 'N/A'}</DebugItem>
              <DebugItem>Premium: {auth.user.is_premium ? 'YES' : 'NO'}</DebugItem>
            </>
          ) : (
            <DebugItem>No user data available</DebugItem>
          )}
        </DebugSection>

        <DebugSection>
          <DebugTitle>📱 Telegram WebApp</DebugTitle>
          <DebugItem>
            WebApp Available: {telegram.tg ? 'YES' : 'NO'}
            {getStatusBadge(!!telegram.tg)}
          </DebugItem>
          <DebugItem>Platform: {telegram.tg?.platform || 'N/A'}</DebugItem>
          <DebugItem>Version: {telegram.tg?.version || 'N/A'}</DebugItem>
          <DebugItem>Color Scheme: {telegram.colorScheme || 'N/A'}</DebugItem>
          <DebugItem>
            Has Init Data: {telegram.authData ? 'YES' : 'NO'}
            {getStatusBadge(!!telegram.authData)}
          </DebugItem>
          <DebugItem>
            Init Data Length: {telegram.authData?.length || 0} chars
          </DebugItem>
          <DebugItem>
            Has User in initDataUnsafe: {telegram.tg?.initDataUnsafe?.user ? 'YES' : 'NO'}
            {getStatusBadge(!!telegram.tg?.initDataUnsafe?.user)}
          </DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>🌐 Environment</DebugTitle>
          <DebugItem>Mode: {process.env.NODE_ENV}</DebugItem>
          <DebugItem>URL: {window.location.href}</DebugItem>
          <DebugItem>User Agent: {navigator.userAgent.substring(0, 50)}...</DebugItem>
          <DebugItem>Is in iframe: {window.parent !== window ? 'YES' : 'NO'}</DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>🔧 Raw Telegram Data</DebugTitle>
          <DebugItem style={{ fontSize: '10px', maxHeight: '200px', overflow: 'auto' }}>
            <pre>{formatJson(telegram.tg?.initDataUnsafe || 'No data')}</pre>
          </DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>📊 Init Data String</DebugTitle>
          <DebugItem style={{ fontSize: '10px', wordBreak: 'break-all' }}>
            {telegram.authData || 'No auth data'}
          </DebugItem>
        </DebugSection>

        <DebugSection>
          <DebugTitle>🎨 Theme Data</DebugTitle>
          <DebugItem style={{ fontSize: '10px' }}>
            <pre>{formatJson(telegram.theme || 'No theme data')}</pre>
          </DebugItem>
        </DebugSection>
      </DebugContainer>
    </>
  );
};